import { createContext, useContext, type ReactNode } from 'react'

/**
 * The default ("agent") canvas id. Mirrors the backend
 * `DEFAULT_CANVAS_ID = "agent"` (agentic_workflow_backend .../core/state_manager.py).
 * This is the single frontend source of truth for the default canvas id — import
 * it here rather than re-declaring the `'agent'` literal.
 */
export const DEFAULT_CANVAS_ID = 'agent'

/**
 * The canvas the workspace is currently viewing (e.g. `"agent"` or a secondary
 * canvas like `"canvas-7bce839e"`).
 *
 * ReactFlow-rendered node components cannot receive the active canvas id as a
 * prop, and REST calls (`executeNode` / `cancelNodeExecution`) must carry it so
 * the backend scopes node lookups to the right per-canvas YDoc room. Without it
 * the backend falls back to `DEFAULT_CANVAS_ID` and any node on a secondary
 * canvas 404s as "Node not found" — see the Regenerate-on-secondary-canvas bug.
 *
 * Defaults to `DEFAULT_CANVAS_ID` when no provider is mounted, so the published
 * project view and the ~80 test render harnesses need no changes.
 */
const ActiveCanvasIdContext = createContext<string>(DEFAULT_CANVAS_ID)

export function ActiveCanvasIdProvider({
  value,
  children,
}: {
  value: string
  children?: ReactNode
}) {
  return (
    <ActiveCanvasIdContext.Provider value={value}>
      {children}
    </ActiveCanvasIdContext.Provider>
  )
}

/**
 * Read the active canvas id. Node components / hooks that call `executeNode` or
 * `cancelNodeExecution` MUST pass this so execution is scoped to the correct
 * canvas.
 */
export function useActiveCanvasId(): string {
  return useContext(ActiveCanvasIdContext)
}
